/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import React from 'react';
import { mapValues, toUpper } from 'lodash';
import { DateTime } from 'luxon';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { action, computed, observable, toJS, runInAction } from 'mobx';

import { getKalturaVideosWatched } from 'services/UserService';
import {
  getManagers,
  getUsers,
  assignManager,
  addManager,
  deleteManager,
  acceptPendingManager,
  rejectPendingManager,
  getOrganizations,
  getAllOrganizations,
  getManagerReportees,
  setManagerState,
  addOrganization,
  deleteOrganization,
  updateOrganization,
  getOrganizationSummary,
  getUserSubscriptionsByOrganizationIds,
  getCustomSkillPaths,
  getConsumptionTimeSeries,
  getCourseCompletionTimeSeries,
  getExamCompletionTimeSeries,
  requestOrganizationSummaryUpdate,
} from 'services/ManagementReportingService';

import { SummaryType } from 'types/organization';
import { createReportSearch } from 'services/ReportingService';
import { postNewJob } from 'services/EventService';
import { Organization, VocabularyNamespaces } from 'types';
import FiltersStore from './common/Filters';

const initialSummaryState = {
  total_courses: 0,
  completed_courses: 0,
  total_lessons: 0,
  completed_lessons: 0,
  vc_courses: 0,
  rol_courses: 0,
  attended_sessions: 0,
  exams: {
    scheduled: 0,
    unscheduled: 0,
    canceled: 0,
    completed: 0,
    retake_available: 0,
    retake_scheduled: 0,
    retake_results_pending: 0,
    retake_complete: 0,
  },
  training_hours: 0,
  lab_hours: 0,
  average_completion_hours: 0,
  average_lesson_completion_hours: 0,
};

type ProgressPerCourseFilterKeys =
  | 'categories'
  | 'modalities'
  | 'products'
  | 'taxonomyProducts'
  | 'taxonomyAudiences'
  | 'taxonomyTopics'
  | 'level';

export type ProgressPerCourseFilters = {
  [K in ProgressPerCourseFilterKeys]?: Record<string, boolean>;
};

type LocalOrganization = Pick<
  Organization,
  'name' | 'id' | 'order_number_set'
> & {
  isPending: boolean;
};

class OrganizationStore {
  rootStore;

  catalogStore;

  uiStore;

  @observable progressPerCourseFilters: ProgressPerCourseFilters = {};

  @observable progressPerCourseTextFilter = '';

  @observable managers = [];

  @observable isLoadingManagers = false;

  @observable users = [];

  @observable isLoadingUsers = false;

  @observable managerReportees = [];

  @observable dedupedManagerReportees = [];

  @observable selectedUsersInManagerDB = new Set<any>(); // For user list on manager dashboard

  @observable selectedUsersInAdminDB = new Set(); // For user list on admin dashboard

  @observable organization: LocalOrganization = {
    name: '',
    id: '',
    isPending: false,
  };

  @observable isLoadingOrganizations = true;

  @observable isOrganizationsTableLoading = false;

  @observable organizations: Array<Organization> = [];

  @observable didFailToFetchOrganizations = false;

  @observable customSkillPaths = [];

  @observable customSkillPathsLoaded = false;

  @observable overallCourseProgress = [];

  @observable kalturaVideosWatched = 0;

  @observable managerProgressFilter = '';

  @observable editingOrganization = {
    id: null,
    name: '',
    orderNumbers: '',
    adminUsers: '',
  };

  @observable _modal = {
    opened: false,
    title: '',
    text: '',
    actionText: 'OK',
    onAction: () => {
      this._modal.opened = false;
    },
  };

  @observable deleteManagerModal = {
    opened: false,
    managerId: '',
    username: '',
  };

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.catalogStore = rootStore.catalogStore;
    this.uiStore = rootStore.uiStore;
  }

  @action setManagerProgressFilter(typed) {
    this.managerProgressFilter = typed;
  }

  @observable entriesFetched = false;

  @observable hasCatalogData = false;

  // eslint-disable-next-line class-methods-use-this
  @observable addHover = (offering) => (
    <OverlayTrigger
      placement="top"
      trigger={['hover']}
      overlay={<Tooltip id="locked-tooltip">{offering.tooltipText}</Tooltip>}
    >
      <span>{offering.text}</span>
    </OverlayTrigger>
  );

  @observable summaryType = SummaryType.All;

  @observable isUsageSummaryLoading = true;

  @observable allSubscriptionsUsageSummary = {
    ...initialSummaryState,
  };

  @observable currentSubscriptionsUsageSummary = {
    ...initialSummaryState,
  };

  @observable customDateRangeSubscriptionsUsageSummary = {
    ...initialSummaryState,
  };

  @observable customDateFromDate = null;

  @observable customDateToDate = null;

  @observable totalHoursTimeSeries = null;

  @observable isTotalHoursTSLoading = true;

  @observable courseCompletionTimeSeries = null;

  @observable isCourseCompletionTSLoading = true;

  @observable isExamCompletionTSLoading = true;

  @observable examCompletionTimeSeries = null;

  @observable summaryPollingId = null;

  @observable summaryLastUpdate = null;

  @computed get usageSummary() {
    return this[`${this.summaryType}SubscriptionsUsageSummary`];
  }

  @action newModal = (modal) => {
    this._modal.opened = true;
    this._modal.title = modal.title;
    this._modal.text = modal.text;
    this._modal.actionText = modal.actionText;
    this._modal.onAction = modal.onAction;
  };

  @computed get modal() {
    return this._modal;
  }

  @action closeModal = () => {
    this._modal.opened = false;
  };

  @action setProgressPerCourseFilters = (filters) => {
    this.progressPerCourseFilters = filters;
  };

  @action setProgressPerCourseTextFilter = (text) => {
    this.progressPerCourseTextFilter = text;
  };

  @action setManagerState = async (managerId, toggleState) => {
    let updatedManager;
    let oldState;
    try {
      this.managers.forEach((manager) => {
        if (manager.uuid === managerId) {
          // Track these in case we need to revert on error
          updatedManager = manager;
          oldState = manager.state;
          runInAction(() => {
            manager.state = toggleState;
          });
        }
      });
      await setManagerState(this.organization.id, managerId, toggleState);
    } catch (err) {
      if (updatedManager) {
        runInAction(() => {
          updatedManager.state = oldState;
        });
      }
      console.error(err);
    }
  };

  @computed get managerList() {
    if (!this.managers || this.managers.length === 0) {
      return [];
    }
    return toJS(this.managers);
  }

  @computed get userList() {
    if (!this.users || this.users.length === 0) {
      return [];
    }
    return toJS(this.users);
  }

  @computed get selectedUsersByManagerList() {
    if (
      !this.selectedUsersInManagerDB ||
      this.selectedUsersInManagerDB.size === 0
    ) {
      return [];
    }

    return [...this.selectedUsersInManagerDB];
  }

  findOrganizationById = (id: string) =>
    this.organizations.find((org) => org.id === id);

  isAdminOfOrg(organization) {
    const currentUser = this.rootStore.userStore.user;

    return Boolean(
      organization?.admin_set?.some((o) => o.uuid === currentUser?.uuid),
    );
  }

  @computed get isAdminOfCurrentlySelectedOrg() {
    return this.isAdminOfOrg(this.organization);
  }

  isManagerOfOrg(organization) {
    const currentUser = this.rootStore.userStore.user;

    return Boolean(
      organization?.manager_set?.some((o) => o.uuid === currentUser?.uuid),
    );
  }

  @computed get isManagerOfCurrentlySelectedOrg() {
    return this.isManagerOfOrg(this.organization);
  }

  @computed get isLeaderOfCurrentlySelectedOrg() {
    return (
      this.isAdminOfCurrentlySelectedOrg || this.isManagerOfCurrentlySelectedOrg
    );
  }

  @action getManagers = async () => {
    try {
      this.isLoadingManagers = true;
      if (this.organization.id) {
        const managerSet = await getManagers(this.organization.id);
        this.setManagers(managerSet.map(this.normalizeManager));
      } else {
        this.setManagers([]);
      }
    } catch (err) {
      console.error('Could not get managers: ', err);
      // TODO Would be nice to show an alert here
    } finally {
      this.isLoadingManagers = false;
    }
  };

  @action getUserByUsername = (username) =>
    this.managerReportees.find(
      (element) => element.username.toLowerCase() === username.toLowerCase(),
    );

  // This should be used to cleanup properies on each manager
  // eslint-disable-next-line class-methods-use-this
  @action normalizeManager = (manager) => {
    if (!manager.preferred_name) {
      manager.preferred_name = `${manager.first_name || ''} ${
        manager.last_name || ''
      }`.trim();
    }
    return manager;
  };

  @action.bound setManagers = (managerList) => {
    this.managers = managerList;
  };

  @action.bound setUsers = (userList) => {
    this.users = userList;
  };

  @action retryLoadingOrganizations = async () => {
    const orgs = await this.getOrganizations();
    this.setOrganization();
    return orgs;
  };

  @action init = async () => {
    try {
      this.isLoadingOrganizations = true;
      const orgs = await this.getOrganizations();
      this.setOrganization();
      this.organizations.forEach(async (org) => {
        await requestOrganizationSummaryUpdate(org.doc_id);
      });
      return orgs;
    } catch (error) {
      if (error) {
        console.error(error);
      }
      this.organizations = [];
      // what else could we do when this fails?
      return this.organizations;
    } finally {
      this.isLoadingOrganizations = false;
    }
  };

  @action setOrganization = (docId?: string) => {
    this.summaryType = SummaryType.All;
    this.resetSummaries();
    if (!docId && this.organizations?.length > 0) {
      this.setOrganization(this.organizations[0].doc_id);
    } else {
      this.organizations?.forEach((org) => {
        if (org.doc_id === docId) {
          this.organization = {
            ...org,
            isPending: org.is_pending || false,
            id: org.doc_id,
          };
        }
      });
    }
  };

  @action addManager = async (username: string, email: string) => {
    try {
      await addManager(username, email, this.organization.id);
    } catch (err) {
      console.error('Could not add manager: ', err);
    }
  };

  @action deleteManager = async (uuid: string) => {
    try {
      await deleteManager(uuid, this.organization.id);
      const remainingManagers = this.managers.filter(
        (manager) => manager.uuid !== uuid,
      );
      runInAction(() => {
        this.setManagers(remainingManagers);
      });
    } catch (err) {
      console.error('Could not delete manager: ', err);
      // TODO Would be nice to show an alert here
    }
  };

  @action addSelectedUserSubscription = (uuid) => {
    this.selectedUsersInAdminDB.add(uuid);
  };

  @action removeSelectedUserSubscription = (uuid) => {
    this.selectedUsersInAdminDB.delete(uuid);
  };

  @action addAllSelectedUsersSubscriptions = (users) => {
    users.forEach((user) => this.selectedUsersInAdminDB.add(user));
  };

  @action removeAllSelectedUsersSubscriptions = () => {
    this.selectedUsersInAdminDB.clear();
  };

  @action getUsers = async () => {
    try {
      this.isLoadingUsers = true;
      if (this.organization.id) {
        const userSet = await getUsers(this.organization.id);
        this.setUsers(userSet);
      } else {
        this.setUsers([]);
      }
    } catch (err) {
      console.error('Could not get users: ', err);
      // TODO Would be nice to show an alert here
    } finally {
      this.isLoadingUsers = false;
    }
  };

  @action setSelectedUserForManager = (items) => {
    this.selectedUsersInManagerDB = new Set(items);
  };

  @action addSelectedUserForManager = (uuid) => {
    this.selectedUsersInManagerDB.add(uuid);
  };

  @action removeSelectedUserForManager = (uuid) => {
    this.selectedUsersInManagerDB.delete(uuid);
  };

  @action addAllSelectedUsersForManager = (users) => {
    users.forEach((user) => this.selectedUsersInManagerDB.add(user));
  };

  @action removeAllSelectedUsersForManager = () => {
    this.selectedUsersInManagerDB.clear();
  };

  @action assignManager = async (managerId, usersToAssign) => {
    await assignManager(this.organization.id, usersToAssign);
    runInAction(() => {
      this.users.forEach((user) => {
        if (usersToAssign.assign.users.includes(user.doc_id)) {
          const oldManager = this.managers.find(
            (manager) => user.org_manager_id === manager.uuid,
          );

          if (oldManager !== undefined) {
            oldManager.reportees -= 1;
          }
          user.org_manager_id = managerId;
        }
      });
      // Update managers to reflect new users
      const newManager = this.managers.find(
        (manager) => managerId === manager.uuid,
      );
      if (newManager !== undefined) {
        newManager.reportees += usersToAssign.assign.users.length;
      }
    });
  };

  @action getReportees = async (isAdmin: boolean = false) => {
    let reportees = [];

    if (isAdmin) {
      reportees = await getUsers(this.organization.id);
    } else {
      reportees = await getManagerReportees();
    }

    const seenEnrollments = new Set();

    this.managerReportees = reportees
      .filter((enrollment) =>
        [...this.organization.order_number_set].includes(
          enrollment.lms_order_number,
        ),
      )

      .sort((a, b) => a.username.localeCompare(b.username));

    this.dedupedManagerReportees = this.managerReportees.filter(
      (enrollment) => {
        if (seenEnrollments.has(enrollment.username)) return false;
        seenEnrollments.add(enrollment.username);
        return true;
      },
    );
  };

  @action acceptPendingManager = async (uuid, organizationId) => {
    const orgId = organizationId ?? this.organization?.id;
    try {
      await acceptPendingManager(uuid, orgId);
      runInAction(() => {
        this.organization.isPending = false;
      });
    } catch (err) {
      throw err;
    }
  };

  @action rejectPendingManager = async (uuid) => {
    try {
      await rejectPendingManager(uuid, this.organization.id);
    } catch (err) {
      console.error('Could not reject manager invitation: ', err);
      // TODO Would be nice to show an alert here
    }
  };

  @action getOrganizations = async () => {
    try {
      this.isLoadingOrganizations = true;
      const { uuid } = this.rootStore.userStore.user;
      if (uuid) {
        const organizations: any = await getOrganizations(uuid);
        this.organizations = organizations.managing;
        this.rootStore.userStore.myOrganizations = organizations.reporting;
      }
      this.didFailToFetchOrganizations = false;
    } catch (err) {
      this.organizations = [];
      this.didFailToFetchOrganizations = true;
      throw err;
    } finally {
      this.isLoadingOrganizations = false;
    }
  };

  @action getAllOrganizations = async ({
    showLoading = true,
  }: { showLoading?: boolean } = {}) => {
    if (showLoading) {
      this.isOrganizationsTableLoading = true;
    }
    try {
      const organizations = await getAllOrganizations();
      this.organizations = organizations.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    } catch (err) {
      throw err;
    } finally {
      if (showLoading) {
        this.isOrganizationsTableLoading = false;
      }
    }
  };

  @computed get organizationsList() {
    return this.organizations;
  }

  @action addOrganization = async (organizationData) => {
    this.isOrganizationsTableLoading = true;
    try {
      await addOrganization(organizationData);
      await this.getAllOrganizations({ showLoading: false });
    } catch (err) {
      throw err;
    } finally {
      this.isOrganizationsTableLoading = false;
    }
  };

  @action updateOrganization = async (uuid, data) => {
    this.isOrganizationsTableLoading = true;
    try {
      await updateOrganization(uuid, data);
      // Backend indexing can lag briefly after updates.
      await new Promise((resolve) => {
        setTimeout(resolve, 5000);
      });
      await this.getAllOrganizations({ showLoading: false });
    } catch (err) {
      throw err;
    } finally {
      this.isOrganizationsTableLoading = false;
    }
  };

  @action deleteOrganization = async (uuid) => {
    this.isOrganizationsTableLoading = true;
    try {
      await deleteOrganization(uuid);
      await new Promise((resolve) => {
        setTimeout(resolve, 5000);
      });
      await this.getAllOrganizations({ showLoading: false });
    } catch (err) {
      throw err;
    } finally {
      this.isOrganizationsTableLoading = false;
    }
  };

  @action editOrganization = (uuid) => {
    this.organizations.forEach((org) => {
      if (org.doc_id === uuid) {
        this.editingOrganization.id = uuid;
        this.editingOrganization.name = org.name;
        this.editingOrganization.orderNumbers = org.order_number_set.join('\n');
        this.editingOrganization.adminUsers = org.admin_set
          .map((a) => a.username)
          .join('\n');
      }
    });
  };

  @action resetEditOrganization = () => {
    this.editingOrganization.id = null;
    this.editingOrganization.name = '';
    this.editingOrganization.orderNumbers = '';
    this.editingOrganization.adminUsers = '';
  };

  @computed get newOrgPayload() {
    const name = this.editingOrganization.name.trim();
    const orderNumbers = this.editingOrganization.orderNumbers
      .split(/[\n,]+/)
      .map((a) => a.trim().toLowerCase().replaceAll('\u200b', ''))
      .filter((a) => a.length);
    const adminUsers = this.editingOrganization.adminUsers
      .split(/[\n,]+/)
      .map((a) => a.trim().toLowerCase())
      .filter((a) => a.length);

    return {
      name,
      orderNumbers,
      adminUsers,
    };
  }

  @computed get hasPendingOrganizations() {
    return this.organizations?.filter((org) => org.is_pending).length >= 1;
  }

  /**
   * Attaches the respective catalog_entry to each courseProgress object.
   * @returns {Array} An array of courseProgress objects with the catalog_entry attached.
   */
  @computed get userProgressPerCourses() {
    const referenceCatalog = this.catalogStore?.allCatalogEntries;

    const data =
      this.usageSummary.user_progress_per_course?.map((courseProgress) => {
        const catalogEntry = referenceCatalog?.find(
          (entry) =>
            entry.slug === courseProgress.id ||
            entry.code === courseProgress.id,
        );

        const courseTitle = catalogEntry?.title
          ? `${catalogEntry?.title}`
          : toUpper(courseProgress.id);

        return {
          ...courseProgress,
          course_title: courseTitle,
          catalog_entry: catalogEntry,
        };
      }) || [];

    return data;
  }

  @computed get progressPerCoursesWithNames() {
    const filterConfigurations = this.catalogStore?.getFilterConfigs();

    let filteredList = FiltersStore.getFilteredEntries<any>(
      this.userProgressPerCourses,
      filterConfigurations,
      this.progressPerCourseFilters,
      (item: any) => item.catalog_entry,
    );

    if (
      this.progressPerCourseTextFilter &&
      this.progressPerCourseTextFilter.length > 0
    ) {
      const lowerCaseFilter = this.progressPerCourseTextFilter.toLowerCase();
      filteredList = filteredList.filter(
        (row) =>
          row.course_title?.toLowerCase().includes(lowerCaseFilter) ||
          row.id?.toLowerCase().includes(lowerCaseFilter),
      );
    }

    return filteredList;
  }

  @computed get filteredManagerProgress() {
    const lowerCaseFilter =
      this.managerProgressFilter?.toLocaleLowerCase() || '';

    if (lowerCaseFilter === '')
      return this.usageSummary.progress_per_manager || [];

    return (
      this.usageSummary.progress_per_manager?.filter(
        (row) =>
          row.manager_name?.toLowerCase().includes(lowerCaseFilter) ||
          row.manager_email?.toLowerCase().includes(lowerCaseFilter) ||
          row.manager_username?.toLowerCase().includes(lowerCaseFilter),
      ) || []
    );
  }

  @action generateManagerReport = (format) => {
    format = format || 'csv';

    createReportSearch({
      organizationId: this.organization.id,
      role: 'managers',
      pull_all: true,
      report_type: 'by_user',
    }).then((searchId) => {
      postNewJob('reporting', `course-reporting-${format}`, {
        searchId,
      });
    });
  };

  @action generateAdminReport = (format) => {
    format = format || 'csv';

    createReportSearch({
      organizationId: this.organization.id,
      role: 'admins',
      report_type: 'by_user',
      pull_all: true,
    }).then((searchId) => {
      postNewJob('reporting', `course-reporting-${format}`, {
        searchId,
      });
    });
  };

  @action async getSummary(
    i18n,
    vocabularyStore,
    fieldName,
    summaryOf,
    dateRange,
    update = true,
  ) {
    const { getVocabularyMapTokensByNamespace } = vocabularyStore;
    const { language } = i18n;
    const data = await getOrganizationSummary(
      this.organization.id,
      this.summaryType,
      summaryOf,
      dateRange,
      update,
    );

    const getTaxonomyProductName = await getVocabularyMapTokensByNamespace(
      language,
      VocabularyNamespaces.OfferingTaxonomyProducts,
    );
    const getCategoryDisplayName = await getVocabularyMapTokensByNamespace(
      language,
      VocabularyNamespaces.OfferingCategories,
    );
    const getTopicDisplayName = await getVocabularyMapTokensByNamespace(
      language,
      VocabularyNamespaces.OfferingTaxonomyTopics,
    );

    // labels are used for the charts
    if (data && data.categories) {
      data.categories = mapValues(data.categories, (value, key) => ({
        value,
        label: getCategoryDisplayName(key),
      }));
    }

    if (data && data.taxonomyProducts) {
      data.taxonomyProducts = mapValues(
        data.taxonomyProducts,
        (value, key) => ({
          value,
          label: getTaxonomyProductName(key),
        }),
      );
    }

    if (data && data.topics) {
      data.topics = mapValues(data.topics, (value, key) => ({
        value,
        label: getTopicDisplayName(key),
      }));
    }

    const { last_update: lastUpdate, ...cleanData } = data;
    if (
      !this.summaryLastUpdate ||
      DateTime.fromISO(lastUpdate) > DateTime.fromISO(this.summaryLastUpdate)
    ) {
      this.summaryLastUpdate = lastUpdate;
    }

    this[fieldName] = { ...this[fieldName], ...cleanData };
  }

  @action getUsageSummary = async (
    i18n,
    vocabularyStore,
    dateRange,
    fetchTrainingHours,
    fetchCompletedCourses,
    fetchCompletedExams,
    fetchLessonSummary,
    update = true,
  ) => {
    const currentSummaryType = this.summaryType;
    const fieldName = `${currentSummaryType}SubscriptionsUsageSummary`;
    if (update) {
      this.summaryLastUpdate = null;
      this.isUsageSummaryLoading = true;
    }

    const summaryUpdates = [];

    if (fetchLessonSummary) {
      summaryUpdates.push(
        this.getSummary(
          i18n,
          vocabularyStore,
          fieldName,
          'course_lesson',
          dateRange,
          update,
        ),
      );
    } else {
      summaryUpdates.push(
        this.getSummary(
          i18n,
          vocabularyStore,
          fieldName,
          'course',
          dateRange,
          update,
        ),
      );
    }

    const summariesList = [
      'exams',
      'lab_hours',
      'attended_sessions',
      'training_hours',
      'student_activity',
    ];
    summariesList.forEach((summaryOf) => {
      // for current dashboard summary, getting exams and course summary details
      // from course summary request only as course and exams summary are dependent
      if (currentSummaryType === SummaryType.Current && summaryOf === 'exams') {
        return;
      }
      summaryUpdates.push(
        this.getSummary(
          i18n,
          vocabularyStore,
          fieldName,
          summaryOf,
          dateRange,
          update,
        ),
      );
    });

    if (fetchTrainingHours) {
      summaryUpdates.push(
        this.getTrainingHoursTimeSeries(
          this.organization.id,
          dateRange,
          update,
        ),
      );
    }
    if (fetchCompletedCourses) {
      summaryUpdates.push(
        this.getCourseCompletionTimeSeries(
          this.organization.id,
          dateRange,
          update,
        ),
      );
    }
    if (fetchCompletedExams) {
      summaryUpdates.push(
        this.getExamCompletionTimeSeries(
          this.organization.id,
          dateRange,
          update,
        ),
      );
    }

    this.getKalturaVideosWatched(this.organization.id, dateRange);

    await Promise.all(summaryUpdates);

    runInAction(() => {
      // It is possible that the sumamry type has been changed before the requests have all completed.
      // If so, the summaries shouldn't appear as if they had loaded, so as to avoid users wrongfully assuming
      // that the data they're seeing is wrong rather than that of a different summary type.
      if (currentSummaryType === this.summaryType) {
        this.isUsageSummaryLoading = false;
      }
    });
  };

  @action switchUsageSummary = async (
    i18n,
    vocabularyStore,
    summaryType,
    dateRange, // assumes dateRange is only defined for customDateRange
    fetchTrainingHours,
    fetchCompletedCourses,
    fetchCompletedExams,
    fetchLessonSummary,
  ) => {
    this.summaryType = summaryType;
    if (summaryType === SummaryType.Custom) {
      // set custom date range params
      this.customDateFromDate = dateRange.from;
      this.customDateToDate = dateRange.to;
    } else {
      // reset custom date range params
      this.customDateFromDate = null;
      this.customDateToDate = null;
    }

    try {
      await this.getUsageSummary(
        i18n,
        vocabularyStore,
        dateRange,
        fetchTrainingHours,
        fetchCompletedCourses,
        fetchCompletedExams,
        fetchLessonSummary,
      );
    } catch (e) {
      console.error(e);
      this.isUsageSummaryLoading = false;
    }
  };

  @action resetSummaries = () => {
    this.allSubscriptionsUsageSummary = { ...initialSummaryState };
    this.currentSubscriptionsUsageSummary = { ...initialSummaryState };
    this.customDateRangeSubscriptionsUsageSummary = { ...initialSummaryState };
  };

  @action filteredOrganizations = (userRole) =>
    this.organizations.filter((org) =>
      org[userRole === 'admin' ? 'admin_set' : 'manager_set']
        .map((user) => user.username)
        .includes(this.rootStore.userStore.user?.username),
    );

  @action getOrganizationName(organizationId) {
    if (!this.organizations.length) return '';
    const _found = this.organizations?.find((o) => o.doc_id === organizationId);
    return _found ? _found.name : '';
  }

  // eslint-disable-next-line class-methods-use-this
  @action getUsersByOrgIdsAndSubscription = async (
    organizationIds = [],
    subscriptionCode = '',
  ) => {
    try {
      if (organizationIds?.length > 0) {
        const dataToSend = {
          org_ids: organizationIds,
          ...(subscriptionCode !== '' && { subscription: subscriptionCode }),
        };

        const userList = await getUserSubscriptionsByOrganizationIds(
          dataToSend,
        );

        return userList;
      }
      return [];
    } catch (err) {
      console.error(
        '[getUsersByOrgIdsAndSubscription] Could not get users for orgids: ',
        err,
      );
      return [];
    }
  };

  @action getCustomSkillPaths = async (organizationId, force = false) => {
    try {
      if ((force || !this.customSkillPathsLoaded) && organizationId) {
        this.customSkillPathsLoaded = false;
        const customSkillPaths = await getCustomSkillPaths(organizationId);
        this.customSkillPaths = customSkillPaths;
      }
    } catch (err) {
      this.customSkillPaths = [];
      console.error(
        '[getCustomSkillPaths] Could not get customskillpats for a orgid: ',
        err,
      );
    } finally {
      this.customSkillPathsLoaded = true;
    }
    return this.customSkillPaths;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  @action getKalturaVideosWatched = async (organizationId, dateRange) => {
    try {
      this.kalturaVideosWatched = await getKalturaVideosWatched();
      // organizationId,
      // this.summaryType,
      // dateRange,
    } catch {
      this.kalturaVideosWatched = 0;
    }
    return this.kalturaVideosWatched;
  };

  @action getTrainingHoursTimeSeries = async (
    organizationId,
    dateRange,
    update = true,
  ) => {
    try {
      this.isTotalHoursTSLoading = update;
      const chartData = await getConsumptionTimeSeries(
        organizationId,
        this.summaryType,
        dateRange,
        update,
      );

      this.totalHoursTimeSeries = chartData;
    } catch (e) {
      console.error(e);
    } finally {
      this.isTotalHoursTSLoading = false;
    }
  };

  @action getCourseCompletionTimeSeries = async (
    organizationId,
    dateRange,
    update = true,
  ) => {
    try {
      this.isCourseCompletionTSLoading = update;
      const chartData = await getCourseCompletionTimeSeries(
        organizationId,
        this.summaryType,
        dateRange,
        update,
      );

      this.courseCompletionTimeSeries = chartData;
    } catch (e) {
      console.error(e);
    } finally {
      this.isCourseCompletionTSLoading = false;
    }
  };

  @action getExamCompletionTimeSeries = async (
    organizationId,
    dateRange,
    update = true,
  ) => {
    try {
      this.isExamCompletionTSLoading = update;
      const chartData = await getExamCompletionTimeSeries(
        organizationId,
        this.summaryType,
        dateRange,
        update,
      );

      this.examCompletionTimeSeries = chartData;
    } catch (e) {
      console.error(e);
    } finally {
      this.isExamCompletionTSLoading = false;
    }
  };
}

export default OrganizationStore;
